/**
 * Page admin pour gérer les accès clients ETA (créer, lister, régénérer).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Copy, RefreshCw, Trash2, KeyRound, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPortailETAPage() {
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [societes, setSocietes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    email: '',
    nom: '',
    entreprise: '',
    societe_id: '',
    password: '',
  });

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/portail-eta/admin/clients`, auth),
        axios.get(`${API}/tenant/societes`, auth).catch(() => ({ data: { societes: [] } })),
      ]);
      setClients(cRes.data.clients || []);
      setSocietes(sRes.data.societes || sRes.data || []);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const creerClient = async () => {
    if (!form.email || !form.nom || !form.societe_id) {
      toast.error('Email, nom et ETA requis');
      return;
    }
    try {
      const r = await axios.post(
        `${API}/portail-eta/admin/creer-client`,
        form,
        auth
      );
      toast.success(
        `Client créé. Mot de passe : ${r.data.mot_de_passe_temporaire}`,
        { duration: 30000 }
      );
      await navigator.clipboard.writeText(
        `Email: ${form.email}\nMot de passe: ${r.data.mot_de_passe_temporaire}\nConnexion: ${window.location.origin}/portail-eta/login`
      );
      setShowForm(false);
      setForm({ email: '', nom: '', entreprise: '', societe_id: '', password: '' });
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création');
    }
  };

  const regenPassword = async (id) => {
    if (!window.confirm('Régénérer le mot de passe ?')) return;
    try {
      const r = await axios.post(
        `${API}/portail-eta/admin/clients/${id}/regen-password`,
        {},
        auth
      );
      toast.success(`Nouveau mot de passe : ${r.data.nouveau_mot_de_passe}`, {
        duration: 30000,
      });
      await navigator.clipboard.writeText(r.data.nouveau_mot_de_passe);
    } catch (err) {
      toast.error('Erreur régénération');
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Supprimer cet accès client ?')) return;
    try {
      await axios.delete(`${API}/portail-eta/admin/clients/${id}`, auth);
      toast.success('Accès supprimé');
      loadAll();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-portail-eta-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Accès Clients ETA</h1>
          <p className="text-slate-500 text-sm mt-1">
            Créez et gérez les comptes de vos clients pour qu'ils consultent
            leurs parcelles, interventions et bons de chantier.
          </p>
        </div>
        <button
          data-testid="btn-creer-client-eta"
          onClick={() => setShowForm(true)}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-lime-500 to-emerald-600 text-white font-semibold flex items-center gap-2 shadow"
        >
          <UserPlus className="w-4 h-4" />
          Nouveau client
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow">
          <h3 className="font-bold mb-4">Créer un accès client</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <input
              data-testid="form-client-email"
              placeholder="Email client"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
            />
            <input
              data-testid="form-client-nom"
              placeholder="Nom & prénom"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
            />
            <input
              data-testid="form-client-entreprise"
              placeholder="Exploitation / Entreprise"
              value={form.entreprise}
              onChange={(e) => setForm({ ...form, entreprise: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
            />
            <select
              data-testid="form-client-societe"
              value={form.societe_id}
              onChange={(e) => setForm({ ...form, societe_id: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
            >
              <option value="">-- ETA rattachée --</option>
              {societes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                </option>
              ))}
            </select>
            <input
              data-testid="form-client-password"
              placeholder="Mot de passe (laisser vide = auto)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 md:col-span-2"
            />
          </div>
          <div className="flex gap-3 mt-4">
            <button
              data-testid="btn-creer-client-submit"
              onClick={creerClient}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold"
            >
              Créer
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-lg bg-slate-200 dark:bg-slate-700"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin" />
      ) : (
        <div className="space-y-3">
          {clients.map((c) => (
            <div
              key={c.id}
              className="p-5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-between"
            >
              <div>
                <div className="font-semibold">{c.entreprise || c.nom}</div>
                <div className="text-sm text-slate-500">{c.email}</div>
                <div className="text-xs text-slate-400 mt-1">
                  ETA : {c.eta_nom || c.societe_id}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(
                      `${window.location.origin}/portail-eta/login`
                    )
                  }
                  className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                  title="Copier URL"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => regenPassword(c.id)}
                  className="p-2 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  title="Régénérer mot de passe"
                >
                  <KeyRound className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteClient(c.id)}
                  className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {clients.length === 0 && (
            <div className="text-slate-500 text-sm">
              Aucun client portail créé. Cliquez sur "Nouveau client" pour commencer.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
